import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Save, Plus, ArrowRight, BarChart2 } from 'lucide-react'
import { Button } from '@/components/ui'
import { useActuals } from '@/hooks/useActuals'

const formatIDR = (amount: number) => {
  return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(amount)
}

export function ActualsPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const navigate = useNavigate()
  const { actuals, isLoading, error, saveActual } = useActuals(businessId)

  // Default to current month YYYY-MM
  const currentMonth = new Date().toISOString().slice(0, 7)
  const [isInputOpen, setIsInputOpen] = useState(false)
  const [formData, setFormData] = useState({
    periodMonth: currentMonth,
    revenue: 0,
    cogs: 0,
    operatingExpense: 0,
    tax: 0,
    interest: 0,
    otherExpense: 0,
    additionalCapital: 0,
    ownerWithdrawal: 0,
    cashBalance: 0,
    inventoryValue: 0,
    receivables: 0,
    payables: 0,
    notes: ''
  })
  const [isSaving, setIsSaving] = useState(false)

  const handleOpenInput = (periodMonth?: string) => {
    if (periodMonth) {
      const existing = actuals.find(a => a.periodMonth === periodMonth)
      if (existing) {
        setFormData({ ...existing, notes: existing.notes || '' })
      }
    } else {
      setFormData(prev => ({ ...prev, periodMonth: currentMonth }))
    }
    setIsInputOpen(true)
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'periodMonth' || field === 'notes' ? value : (Number(value) || 0)
    }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    const res = await saveActual(formData)
    setIsSaving(false)
    if (res.success) {
      setIsInputOpen(false)
    } else {
      alert(res.error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p>Memuat data aktual...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-20">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Laporan Aktual Bulanan</h1>
          <p className="text-sm text-slate-400 mt-1">Catat realisasi keuangan bulan ini untuk dibandingkan dengan simulasi.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate(`/businesses/${businessId}/actuals/compare`)} variant="outline" className="gap-2">
            <BarChart2 size={16} /> Bandingkan Simulasi
          </Button>
          <Button onClick={() => handleOpenInput()} className="gap-2">
            <Plus size={16} /> Input Bulan Baru
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
          <p>{error}</p>
        </div>
      )}

      {isInputOpen && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-200 mb-4">Input Data Aktual</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div className="col-span-full md:col-span-1">
              <label className="block text-xs text-slate-400 mb-1">Bulan Periode (YYYY-MM)</label>
              <input type="month" value={formData.periodMonth} onChange={e => handleChange('periodMonth', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" />
            </div>

            <div className="col-span-full">
              <h3 className="text-sm font-medium text-slate-300 mt-2 mb-2 border-b border-slate-800 pb-1">Profit & Loss (P&L)</h3>
            </div>
            
            <div>
              <label className="block text-xs text-emerald-400/70 mb-1">Revenue / Pendapatan</label>
              <input type="number" value={formData.revenue || ''} onChange={e => handleChange('revenue', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-emerald-400" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-rose-400/70 mb-1">COGS / Harga Pokok</label>
              <input type="number" value={formData.cogs || ''} onChange={e => handleChange('cogs', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-rose-400" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-rose-400/70 mb-1">Operating Expense (Opex)</label>
              <input type="number" value={formData.operatingExpense || ''} onChange={e => handleChange('operatingExpense', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-rose-400" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Pajak (Tax)</label>
              <input type="number" value={formData.tax || ''} onChange={e => handleChange('tax', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Bunga (Interest)</label>
              <input type="number" value={formData.interest || ''} onChange={e => handleChange('interest', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" placeholder="0" />
            </div>

            <div className="col-span-full">
              <h3 className="text-sm font-medium text-slate-300 mt-2 mb-2 border-b border-slate-800 pb-1">Balance Sheet & Cash</h3>
            </div>

            <div>
              <label className="block text-xs text-blue-400/70 mb-1">Ending Cash Balance</label>
              <input type="number" value={formData.cashBalance || ''} onChange={e => handleChange('cashBalance', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-blue-400" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Inventory Value</label>
              <input type="number" value={formData.inventoryValue || ''} onChange={e => handleChange('inventoryValue', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Piutang (Receivables)</label>
              <input type="number" value={formData.receivables || ''} onChange={e => handleChange('receivables', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Utang (Payables)</label>
              <input type="number" value={formData.payables || ''} onChange={e => handleChange('payables', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" placeholder="0" />
            </div>

            <div className="col-span-full md:col-span-2">
              <label className="block text-xs text-slate-400 mb-1">Catatan</label>
              <input type="text" value={formData.notes || ''} onChange={e => handleChange('notes', e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-2 text-slate-200" placeholder="Ada pengeluaran tak terduga..." />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsInputOpen(false)}>Batal</Button>
            <Button onClick={handleSave} disabled={isSaving} className="gap-2">
              <Save size={16} /> {isSaving ? 'Menyimpan...' : 'Simpan Data'}
            </Button>
          </div>
        </div>
      )}

      {/* History Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-800">
          <h2 className="text-lg font-semibold text-slate-200">Riwayat Aktual</h2>
        </div>
        {actuals.length === 0 ? (
          <div className="p-10 text-center text-slate-500">Belum ada data aktual yang dicatat.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-slate-800/50 text-slate-300 border-b border-slate-700">
                <tr>
                  <th className="px-4 py-3 font-medium">Bulan</th>
                  <th className="px-4 py-3 font-medium text-emerald-400">Revenue</th>
                  <th className="px-4 py-3 font-medium text-rose-400">Total Biaya (COGS+Opex)</th>
                  <th className="px-4 py-3 font-medium text-blue-400">Kas Akhir</th>
                  <th className="px-4 py-3 font-medium">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {actuals.map(a => (
                  <tr key={a.id} className="hover:bg-slate-800/30 transition-colors text-slate-300">
                    <td className="px-4 py-3 font-medium">{a.periodMonth}</td>
                    <td className="px-4 py-3 text-emerald-400">{formatIDR(a.revenue)}</td>
                    <td className="px-4 py-3 text-rose-400">{formatIDR(a.cogs + a.operatingExpense)}</td>
                    <td className="px-4 py-3 text-blue-400">{formatIDR(a.cashBalance)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleOpenInput(a.periodMonth)} className="text-brand-400 hover:text-brand-300 underline">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
